import { Avatar, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { GenericAbortSignal } from "axios";
import { MprcTypography } from "components";
import { ManagedEfficacySurveyParticipantsResponse, ManagedEfficacySurveyParticipantsResponsePaginatedList } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps } from "react-window";
import { GenericListDrawer, GenericListFilter, GenericListItemsType } from "views/common/GenericListDrawer";
import { LmsTrainingDefaultThumbnailUrl } from "views/lms/components/LmsTypeImage";

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    userId: string;
    title: string;
    onClose: (item?: ManagedEfficacySurveyParticipantsResponse) => void;
}

type ItemData = GenericListItemsType<ManagedEfficacySurveyParticipantsResponse>;

export const SelectEfficacySurveyDrawer = ({ userId, title, onClose }: IProps) => {
    const api = useCoreApi();
    const { t } = useTranslation("assessment");

    const handleGetData = (genericFilter: GenericListFilter, signal: GenericAbortSignal | undefined): Promise<ManagedEfficacySurveyParticipantsResponsePaginatedList> => {
        return new Promise((resolve, reject) => {
            api.v1.managerEfficacy.managerEfficayParticipants(
                {
                    userId,
                    ...genericFilter
                },
                { signal }
            )
                .then((response) => resolve(response.data))
                .catch(reject);
        });
    };

    const renderRow = (props: ListChildComponentProps<ItemData>) => {
        const { index, style } = props;
        const { items, onViewerClick } = props.data;
        const item = items[index];

        return (
            <ListItem key={index} style={style} disableGutters onClick={() => onViewerClick && onViewerClick(item)} className={AppGlobalClassKeys.clickable}>
                <ListItemAvatar>
                    <Avatar
                        variant="rounded"
                        src={LmsTrainingDefaultThumbnailUrl(item.contentType, item.thumbnailUrl)}
                        sx={(theme) => ({ width: 42, height: 30, backgroundColor: theme.palette.grey[200] })}
                    />
                </ListItemAvatar >
                <ListItemText
                    disableTypography
                    primary={<MprcTypography noWrapRows={1} variant="body2" fontWeight="bold" title={item.trainingName}>{item.trainingName}</MprcTypography>}
                    secondary={
                        item.answeredOn != null
                            ? <MprcTypography noWrapRows={1} variant="caption" color="success.main">{Formaters.performaDate(item.answeredOn, { preText: t("ManagerEfficacy.SelectEfficacySurveyDrawer.AnsweredText") })}</MprcTypography>
                            : item.expired
                                ? <MprcTypography noWrapRows={1} variant="caption" color="error.main">{t("ManagerEfficacy.SelectEfficacySurveyDrawer.ExpiredText")}</MprcTypography>
                                : item.validUntil
                                    ? <MprcTypography noWrapRows={1} variant="caption" color="muted.main">{Formaters.performaDate(item.validUntil, { preText: t("ManagerEfficacy.SelectEfficacySurveyDrawer.ValidUntil"), preposition: "none" })}</MprcTypography>
                                    : "?"
                    }
                />
            </ListItem>
        );
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return <GenericListDrawer
        title={t("ManagerEfficacy.SelectEfficacySurveyDrawer.Title")}
        subtitle={title}
        itemHeight={48}
        onClose={onClose}
        onGetData={handleGetData}
        renderRow={renderRow}
        onViewerClick={(item) => onClose(item)}
        recordCounterFormater={(count) => t("ManagerEfficacy.SelectEfficacySurveyDrawer.RecordCounter", { count })}
    />;
};
